import { Component, HostListener, OnInit, OnDestroy, AfterViewInit, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { MenuItem, MenuItemClickType, MenuItemType } from './menu-item/menu-item';
import { MenuItemComponent } from './menu-item/menu-item.component';
import { MainMenuService } from '../../../services/main-menu.service';
import { Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntil } from 'rxjs';
import { moveItemInArray, CdkDropList, CdkDrag, CdkDragDrop } from '@angular/cdk/drag-drop';
import { OktaAuthService } from '../../../services/okta-auth.service';
import { UserService } from '../../../services/user-service.service';
import { Router, NavigationStart, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import Shepherd from 'shepherd.js';
import { GuidedTourService } from '../../apps/shared/guided-tour.service';
import { PostsTourService } from '../../apps/posts/posts-tour.service';
import { AppService } from '../../../services/app.service';
import { ServiceStatus } from '../../apps/shared/service-status.enum';
import { checkIsWebView } from '../../../helpers/screen-utils';
import { AppsHeaderComponent } from '../../apps/apps-header/apps-header.component';
import { UserStatusComponent } from '../../apps/shared/user-status.component';
import { CriticalAlertsComponent } from '../../apps/shared/critical-alerts/critical-alerts.component';
import { NgStyle, CurrencyPipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { AppsFooterComponent } from '../../apps/apps-footer/apps-footer.component';
import { HybridStorageKeys, HybridStorageService } from '../../services/hybrid-storage.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RtI18nDirective } from '../../apps/shared/runtime-translation.directive';
import { UserProfileService } from '../../../services/user-profile.service';
import { AdaptiveContainerService } from '../../apps/shared/adaptive-container/adaptive-container.service';
import { CommunicationPreferencesModalComponent } from '../../apps/shared/modal/communication-preferences-modal/communication-preferences-modal.component';
import { RegistrationCompleteModalComponent } from '../../apps/shared/modal/registration-complete-modal/registration-complete-modal.component';
import { GradeLevelOption, gradeOptionsForSchool } from '../../apps/shared/grade-levels';
import { academicYearPillLabel } from '../../apps/shared/academic-year-label';
import { CommunicationConsentService } from '../../../services/communication-consent.service';
import { UserImageComponent } from '../../apps/shared/user-image/user-image.component';
import { StudentInfo } from '../../apps/student/models/StudentInfo';
import { GuardianTodosResult, TodoItem } from '../../apps/student/models/Todo';
import { TodosSectionComponent } from '../../apps/shared/todos-section/todos-section.component';
import { SchoolCardComponent } from '../../apps/shared/school-card/school-card.component';
import { PortalLinkService } from '../../../services/portal-link.service';
import { StudentInSchoolRecord } from '../../apps/student/models/StudentInSchoolRecord';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { School } from '../../models/school.model';
import { SchoolsService } from '../../apps/shared/schools.service';
import { SchoolSelectorComponent } from '../../apps/shared/school-selector/school-selector.component';
import { EnrollmentMasterStatus } from '../../apps/student/models/EnrollmentMasterStatus';
import { EnrollmentCardStage, determineCardStage } from '../../apps/student/models/EnrollmentCardStage';

@Component({
    selector: 'app-main-menu',
    standalone: true,
    templateUrl: './main-menu.component.html',
    styleUrl: './main-menu.component.scss',
    imports: [
      AppsHeaderComponent,
      UserStatusComponent,
      CriticalAlertsComponent,
      NgStyle,
      CurrencyPipe,
      CdkDropList,
      MenuItemComponent,
      CdkDrag,
      MatProgressSpinner,
      AppsFooterComponent,
      TranslateModule,
      RtI18nDirective,
      MatTooltip,
      MatMenuTrigger,
      MatMenu,
      MatMenuItem,
      MatDivider,
      MatTabsModule,
      UserImageComponent,
      SchoolSelectorComponent,
      TodosSectionComponent,
      SchoolCardComponent
    ]
})
export class MainMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren(MenuItemComponent) menuItemComponents!: QueryList<MenuItemComponent>;

  // Enums
  readonly ServiceStatus = ServiceStatus;
  readonly MenuItemType = MenuItemType;
  readonly EnrollmentCardStage = EnrollmentCardStage;
  readonly EnrollmentMasterStatus = EnrollmentMasterStatus;

  // Properties
  userFirstName: string = "";
  profileMenuItem: MenuItem;
  oktaUserId: string;
  menuItems?: MenuItem[];
  isEditMode: boolean = false;
  isWebView: boolean = false;
  menuOuterHeight: string = '100%';
  parentPortalStudents: StudentInfo[] = [];
  // A logged-in student's own portal record (guardians never load this) — gates the student-only
  // College Readiness tile.
  selfPortalStudent: StudentInfo | null = null;
  schools: School[] = [];

  // Active slide per mobile carousel, driven by scroll position (see .carousel-dots).
  studentCarouselIndex: number = 0;
  schoolCarouselIndex: number = 0;

  // Sidebar school selection for staff with more than one school (fed by the shared
  // app-school-selector, which persists it as the app-wide selected school). Guardians and
  // single-school staff never set this — they see a card per school instead.
  selectedSchool: School | null = null;

  // The guardian's To Dos cards for the sidebar. The section renders a spinner while
  // loading, then the cards; when there's nothing to show (including after a failed fetch —
  // fail-soft, like the volunteer status) the whole section hides (app-todos-section decides).
  todoItems: TodoItem[] = [];
  todosServiceStatus = ServiceStatus.unset;

  // Service Props
  menuItemsServiceStatus = ServiceStatus.unset;
  parentPortalStudentServiceStatus = ServiceStatus.unset;
  menuItemsError?: HttpErrorResponse;
  private destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef,
    public mainMenuService: MainMenuService,
    public appService: AppService,
    public userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private oktaAuthService: OktaAuthService,
    private hybridStorageService: HybridStorageService,
    private userProfileService: UserProfileService,
    private translate: TranslateService,
    private guidedTourService: GuidedTourService,
    private postsTourService: PostsTourService,
    private adaptiveDialog: AdaptiveContainerService,
    private consentService: CommunicationConsentService,
    private schoolsService: SchoolsService,
    private portalLink: PortalLinkService
  ) {

    this.profileMenuItem = {
      ItemType: MenuItemType.Profile,
      Id: -1,
      Title: "Profile",
      SortOrder: -1,
      Notifications: 0, 
      IconPath: undefined,
      ClickType: MenuItemClickType.Main,
      CanBeHidden: false,
      ClickPath: 'profiles',
    };
  }

  // #region Lifecycle Hooks
  ngAfterViewInit(): void {
    setTimeout(() => this.updateDesktopHeight());
  }

  async ngOnInit() {
    this.checkScreen();

    // The nav bar's + slot lands here from other pages asking to open edit mode directly.
    if (this.route.snapshot.queryParamMap.has('editApps')) {
      this.isEditMode = true;
      // Strip the flag so a refresh or back-navigation doesn't reopen edit mode.
      this.router.navigate([], { queryParams: { editApps: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }

    // MainMenuService owns the menu cache; subscribe before loading so the cached structure
    // paints as soon as it hydrates.
    this.mainMenuService.menuItems$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(items => {
      if (items) {
        this.menuItems = items;
      }
    });

    this.router.events.pipe(
      filter(e => e instanceof NavigationStart),
      takeUntil(this.destroy$)
    ).subscribe((e: NavigationStart) => {
      if (e.url.startsWith('/apps/profiles') && Shepherd.activeTour?.getCurrentStep()?.id === 'profile-icon') {
        Shepherd.activeTour.cancel();
        this.guidedTourService.setPendingTour('profile-setup');
      }
    });

    await this.loadLocalStorageData();
    await this.loadMainMenuItems();

    this.profileMenuItem.Title = this.translate.instant('mainMenu.myProfile');

    this.showCommunicationPreferencesIfConsentMissing();
    this.loadParentPortalStudents();
    this.loadSelfPortalStudent();
    this.loadTodos();
    this.loadSchools();
  }

  /** Whether the current user is a guardian — the To Dos section only exists for guardians. */
  get isGuardian(): boolean {
    return this.userService.CurrentUserProfile?.UserType === 'Guardian';
  }

  /** Whether the current user is a student looking at their own menu. */
  get isStudent(): boolean {
    return this.userService.CurrentUserProfile?.UserType === 'Student';
  }

  /**
   * Whether the logged-in student gets the College Readiness tile — the same gate the guardian
   * view uses on a student's page (StudentComponent.showCollegeReadiness): the student's current
   * school record is flagged college-readiness eligible. The server also feature-flags this
   * (ParentPortal:EnableCollegeReadiness); while off it strips the flag from every record, so
   * this stays false app-wide.
   */
  get showCollegeReadiness(): boolean {
    if (!this.isStudent) return false;
    return this.currentSelfRecord?.IsCollegeReadinessEligible ?? false;
  }

  /**
   * The logged-in student's current school record: the year the gateway flagged current,
   * otherwise their active enrollment (Enrolled, then Re-Enrolled) — the same pick order the
   * student page's header uses.
   */
  private get currentSelfRecord(): StudentInSchoolRecord | null {
    const records = this.selfPortalStudent?.StudentInSchoolRecords;
    if (!records?.length) return null;
    return records.find(r => r.IsCurrentYear)
      ?? records.find(r => r.MasterStatusID === EnrollmentMasterStatus.Enrolled)
      ?? records.find(r => r.MasterStatusID === EnrollmentMasterStatus.ReEnrolled)
      ?? null;
  }

  /**
   * College Readiness rendered as a dashboard app tile, matching the one a guardian sees on the
   * student's page. Defined client-side (there's no dbo.Apps row for it, so the server-driven
   * menu can't carry it); menu-item's Student click type navigates to
   * /apps/students/{studentUserId}/college-readiness, falling back to the logged-in student's
   * own id since there's no hosting student page here. Rendered outside menuItems so it never
   * lands in the drag/hide persistence, which is keyed by real app ids.
   */
  readonly collegeReadinessApp: MenuItem = {
    ItemType: MenuItemType.App,
    Id: -1,
    Title: 'College Readiness',
    IconPath: '/assets/images/app-icons/graduation.svg',
    ClickPath: 'college-readiness',
    ClickType: MenuItemClickType.Student,
    SortOrder: 0,
    Notifications: 0,
    CanBeHidden: false,
  };

  /**
   * Whether the re-enrollment card shows at the top of the To Dos list. Mirrors the portal's
   * home-page banner rules, both driven by the student payload we already load: the family is
   * inside the config-driven re-enrollment window (showReenrollmentAlert — the server sets the
   * same value on every student) AND has at least one Enrolled student.
   */
  get showReenrollmentTodo(): boolean {
    return this.parentPortalStudents.some(s => s.ShowReenrollmentAlert)
      && this.parentPortalStudents.some(s =>
        s.StudentInSchoolRecords?.some(r => r.MasterStatusID === EnrollmentMasterStatus.Enrolled));
  }

  /**
   * Loads the guardian's Parent Portal to-dos for the sidebar's To Dos section; no-ops for
   * non-guardian users. Fail-soft: on error the section just shows its empty state.
   */
  private loadTodos(): void {
    if (!this.isGuardian) return;

    this.todosServiceStatus = ServiceStatus.loading;

    this.mainMenuService.getTodosByGuardianAsync().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: GuardianTodosResult) => {
        // IsPortalDataUnavailable = true arrives with an empty list; the section hides, the
        // same as a real empty — but the server no longer reports an outage as "no to-dos".
        this.todoItems = result?.Todos ?? [];
        this.todosServiceStatus = ServiceStatus.loaded;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading todos:', error);
        this.todoItems = [];
        this.todosServiceStatus = ServiceStatus.error;
      }
    });
  }

  /** "2026-2027" or "2026-27" → "2026–27" for the status pills; falls back to the raw name. */
  yearPillLabel(yearName?: string): string {
    return academicYearPillLabel(yearName);
  }

  /**
   * Loads the schools the user is allowed to see for the sidebar cards. SchoolsService is
   * cache-first: it emits the locally stored list immediately (when present) and refreshes
   * from the API in the background.
   */
  private loadSchools(): void {
    this.schoolsService.getSchoolsForUser().pipe(
      takeUntil(this.destroy$)
    ).subscribe(schools => {
      this.schools = schools ?? [];
    });
  }

  /**
   * Staff with access to many schools (e.g. service-center staff who can see every school)
   * get a school selector in the sidebar instead of a card per school. Staff with 3 or fewer
   * schools get the same tabbed carousel guardians do.
   */
  get showStaffSchoolSelector(): boolean {
    return this.userService.CurrentUserProfile?.UserType === 'Staff' && this.schools.length > 3;
  }

  /**
   * The schools the sidebar renders cards for: all of them normally (guardians get a
   * carousel), or just the selected one when the staff school selector is shown (none until
   * its initial selection resolves).
   */
  get displayedSchools(): School[] {
    if (!this.showStaffSchoolSelector) return this.schools;
    if (!this.selectedSchool) return [];
    const selected = this.schools.find(s => s.Id === this.selectedSchool!.Id);
    return selected ? [selected] : [this.selectedSchool];
  }

  /**
   * Sidebar selector change (also fires once on init with the restored/default selection).
   * The selector itself persists the choice app-wide via SchoolsService; here we only swap
   * the rendered card (app-school-card resolves its own links).
   */
  onSidebarSchoolChanged(school: School): void {
    this.selectedSchool = school;
  }

  /**
   * Loads the guardian's parent-portal students; no-ops for non-guardian users.
   * Stale-while-revalidate: students cached by a previous visit (restored in
   * loadLocalStorageData) stay on screen and the fetch always runs to replace them, so the
   * loading spinner only ever shows on a cache miss. Unlike the menu-structure cache there is
   * no freshness window — the external portal data is re-fetched on every visit.
   */
  private loadParentPortalStudents(): void {
    if (this.userService.CurrentUserProfile?.UserType !== 'Guardian') return;

    // Only spin when there's nothing cached to show.
    if (this.parentPortalStudents.length === 0) {
      this.parentPortalStudentServiceStatus = ServiceStatus.loading;
    }

    this.mainMenuService.getParentPortalsStudentsByGuardianAsync().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: async (students: StudentInfo[]) => {
        this.parentPortalStudents = students;
        this.rebuildVisibleRecords();
        await this.hybridStorageService.setItem(HybridStorageKeys.MainMenu_ParentPortalStudents, students);
        this.parentPortalStudentServiceStatus = ServiceStatus.loaded;
      },
      error: (error: HttpErrorResponse) => {
        // Non-critical: keep showing any cached students and only surface the error state
        // when there was nothing to show in the first place.
        console.error('Error loading parent portal students:', error);
        if (this.parentPortalStudents.length === 0) {
          this.parentPortalStudentServiceStatus = ServiceStatus.error;
        }
      }
    });
  }
  
  /**
   * Loads a logged-in student's own parent-portal record; no-ops for every other user type.
   * Students have no student cards to fill — this is only here so the menu knows which
   * student-only tiles they qualify for (College Readiness). Stale-while-revalidate against the
   * cached copy restored in loadLocalStorageData, and fail-soft: on error we keep whatever was
   * cached rather than dropping a tile the student had a moment ago.
   */
  private loadSelfPortalStudent(): void {
    const userId = this.userService.CurrentUserProfile?.Id;
    if (!this.isStudent || !userId) return;

    this.mainMenuService.getOwnParentPortalStudentAsync(userId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: async (student: StudentInfo | null) => {
        if (!student) return;
        this.selfPortalStudent = student;
        await this.hybridStorageService.setItem(HybridStorageKeys.MainMenu_SelfPortalStudent, student);
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading student portal record:', error);
      }
    });
  }

  /**
   * Pop the communication-preferences dialog whenever a guardian has a current-year school with no
   * consent on file (checked server-side, keyed by their phone number). Unlike a one-time first-login
   * flag this re-triggers when a child advances to a new school or the guardian changes their phone.
   * It blocks until the user answers and saves: no backdrop click, no Esc, only a Save button.
   * Adaptive: centered dialog on desktop, bottom sheet on mobile.
   */
  private showCommunicationPreferencesIfConsentMissing(): void {
    // Communication-consent modal applies to guardians only.
    if (this.userService.CurrentUserProfile?.UserType !== 'Guardian') return;

    this.consentService.hasMissingConsent().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (missing) => {
        if (!missing) return;
        this.adaptiveDialog.open(CommunicationPreferencesModalComponent, {
          width: '800px',
          disableClose: true
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // #endregion

  // #region Utilities
  @HostListener('window:resize')
  checkScreen() {
    this.isWebView = checkIsWebView();
    this.updateDesktopHeight();
  }

  // Mobile carousel dots: derive the active slide from scroll position. The 12px
  // gap must match the carousel gap in the scss.
  onCarouselScroll(carousel: HTMLElement, type: 'students' | 'schools'): void {
    const slide = carousel.querySelector<HTMLElement>('.student-card-stack, .school-stack');
    if (!slide) {
      return;
    }
    const index = Math.max(0, Math.round(carousel.scrollLeft / (slide.offsetWidth + 12)));
    if (type === 'students') {
      this.studentCarouselIndex = index;
    } else {
      this.schoolCarouselIndex = index;
    }
  }

  scrollCarouselTo(carousel: HTMLElement, index: number): void {
    const slide = carousel.querySelector<HTMLElement>('.student-card-stack, .school-stack');
    if (!slide) {
      return;
    }
    carousel.scrollTo({ left: index * (slide.offsetWidth + 12), behavior: 'smooth' });
  }

  private updateDesktopHeight(): void {
    if (window.innerWidth <= 768) {
      this.menuOuterHeight = '100%';
      return;
    }
    const host = this.el.nativeElement as HTMLElement;
    let aboveHeight = 0;
    for (const child of Array.from(host.children) as HTMLElement[]) {
      if (!child.classList.contains('menu-outer')) {
        aboveHeight += child.offsetHeight;
      }
    }
    this.menuOuterHeight = `calc(100dvh - ${aboveHeight}px)`;
  }

  get showGuidedTour(): boolean {
    return this.userService.CurrentUserProfile?.ShowGuidedTour ?? false;
  }

  get isImpersonating(): boolean {
    // No single signal is reliable on its own: impersonation is account-wide
    // (dbo.Impersonations), so it can be active without this browser having started it, and the
    // impersonated guardian may have never activated (NULL OktaUserId), which blinds an id diff.
    // Server-resolved flag (refreshUserProfile) covers impersonations started anywhere; the user
    // service's local state covers one started here before that refresh lands; the id diff stays
    // as a fallback for cached profiles that predate the flag.
    return !!this.userService.CurrentUserProfile?.IsImpersonating ||
      this.userService.IsImpersonating ||
      !!(this.oktaUserId && this.userService.CurrentUserProfile?.OktaUserId &&
        this.oktaUserId !== this.userService.CurrentUserProfile.OktaUserId);
  }

  get canAccessMessages(): boolean {
    return this.userService.CurrentUserProfile?.Roles.some(r => r.RoleName == 'AppAccess_Messages') ?? false;
  }

  get canAccessDirectory(): boolean {
    return this.userService.CurrentUserProfile?.Roles.some(r => r.RoleName === 'AppAccess_Directory') ?? false;
  }

  get canAccessCalendar(): boolean {
    return this.userService.CurrentUserProfile?.Roles.some(r => r.RoleName === 'AppAccess_Calendar') ?? false;
  }

  get canCreatePost(): boolean {
    const profile = this.userService.CurrentUserProfile;
    return (profile?.Roles.some(r => r.RoleName === 'AppAccess_Posts') ?? false) &&
           profile?.UserType === 'Staff';
  }

  get canViewPostsReadOnly(): boolean {
    const profile = this.userService.CurrentUserProfile;
    return (profile?.Roles.some(r => r.RoleName === 'AppAccess_Posts') ?? false) &&
           profile?.UserType !== 'Staff';
  }

  get hiddenApps(): MenuItem[] {
    return this.menuItems?.filter(i => i.SortOrder < 0) ?? [];
  }

  get canViewExpandedError(): boolean {
    return this.userService.CurrentUserProfile?.Roles?.some(r => r.RoleName === 'SDR') ?? false;
  }

  async loadLocalStorageData() {
    const storedFirstName = await this.hybridStorageService.getItem<string>(HybridStorageKeys.MainMenu_UserFirstName);
    if (storedFirstName) {
      this.userFirstName = storedFirstName;
    }

    // Menu items hydrate from the same cache via MainMenuService.ensureMenuItemsLoaded(), which
    // this component subscribes to in ngOnInit.

    // Cached parent-portal students are user-specific but safe to restore blindly: the key is in
    // HybridStorageKeys.impersonateStorageKeys(), so impersonation start/stop wipes it, and only
    // guardians ever persist it.
    const storedStudents = await this.hybridStorageService.getItem<StudentInfo[]>(HybridStorageKeys.MainMenu_ParentPortalStudents);
    if (storedStudents) {
      this.parentPortalStudents = storedStudents;
      this.rebuildVisibleRecords();
      this.parentPortalStudentServiceStatus = ServiceStatus.loaded;
    }

    // Same deal for a student's own cached portal record: impersonation wipes the key, and only
    // students ever persist it. Restoring it paints their College Readiness tile immediately
    // instead of waiting on the external portal call.
    const storedSelfStudent = await this.hybridStorageService.getItem<StudentInfo>(
      HybridStorageKeys.MainMenu_SelfPortalStudent);
    if (storedSelfStudent) {
      this.selfPortalStudent = storedSelfStudent;
    }
  }
  // #endregion

  // #region Services
  async loadMainMenuItems() {
    this.oktaAuthService.oktaUserId$.subscribe({
      next: (result) => {
        this.oktaUserId = result;
      }
    });

    await this.refreshUserProfile();

    await this.loadMenuItems();
  }

  async refreshUserProfile() {
    // Fetch the authenticated user's profile directly from API using Okta ID
    // This ensures we always get the actual logged-in user's name, not an impersonated user's name
    this.userProfileService.getUserProfileByOktaUserId().subscribe({
      next: async (userProfile) => {
        this.userFirstName = userProfile.FirstName;
        await this.hybridStorageService.setItem(HybridStorageKeys.MainMenu_UserFirstName, this.userFirstName);

        // Update the user service with the authenticated user's profile
        await this.userService.setCurrentUserProfile(userProfile);
      },
      error: (error) => {
        console.error('Error fetching authenticated user profile:', error);
        // Fallback to cached profile if API call fails
        const currentUser = this.userService.CurrentUserProfile;
        if (currentUser) {
          this.userFirstName = currentUser.FirstName;
        }
      }
    });
  }

  async loadMenuItems() {
    this.menuItemsError = undefined;
    this.menuItemsServiceStatus = ServiceStatus.loading;

    // MainMenuService serves the structure from cache while it's fresh and re-fetches only when
    // it's missing or stale, so navigating back here doesn't re-query the whole menu. The
    // menuItems$ subscription in ngOnInit applies the result.
    try {
      const servedFromCache = await this.mainMenuService.ensureMenuItemsLoaded();
      this.menuItemsServiceStatus = ServiceStatus.loaded;
      if (servedFromCache) {
        this.refreshNotificationCounts();
      }
    } catch (error) {
      this.menuItemsError = error as HttpErrorResponse;
      this.menuItemsServiceStatus = ServiceStatus.error;
    }

    // check if firstname is empty
    if(!this.userFirstName) {
      this.refreshUserProfile();
    }
  }

  /**
   * Refreshes only the per-app notification badge counts on the already-loaded menu items
   * (e.g. when returning to a main menu served from cache), without re-fetching the whole
   * menu structure. No-ops if there are no menu items yet.
   */
  refreshNotificationCounts() {
    if (!this.menuItems) {
      return;
    }

    this.mainMenuService.getAppNotificationCounts().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (counts: Record<number, number>) => {
        if (!this.menuItems) {
          return;
        }
        // Update the live badges only — intentionally NOT persisted, so the structure cache keeps
        // its original freshness timestamp (absolute TTL) rather than sliding forward every visit.
        this.applyNotificationCounts(this.menuItems, counts);
      },
      error: (error: HttpErrorResponse) => {
        // Non-critical: stale badges are acceptable until the next successful refresh.
        console.error('Error refreshing notification counts:', error);
      }
    });
  }

  /**
   * Applies AppId→count badge values to App menu items. Apps with no entry in the map are
   * reset to 0 so cleared badges disappear. Students are untouched.
   */
  private applyNotificationCounts(items: MenuItem[], counts: Record<number, number>) {
    for (const item of items) {
      if (item.ItemType === MenuItemType.App) {
        item.Notifications = counts[item.Id] ?? 0;
      }
    }
  }

  // #endregion

  // #region Drag and Drop / Edit Mode
  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
  }

  /**
   * Posts the edit and keeps the cached structure in sync (resetting its freshness window) so a
   * cache-served menu doesn't revert the change on the next visit.
   */
  async updateMenuItems(menuItems: MenuItem[]) {
    await this.mainMenuService.saveMenuItems(menuItems);
  }

  onToggleVisibilityClick(menuItem: MenuItem): void {
    if (menuItem.SortOrder === -1) {
      this.unhide(menuItem);
    } else {
      menuItem.SortOrder = -1;
      // The whole list, not just this item: the server renumbers nav-row slots over whatever it
      // receives, so a single-item post would collapse the row onto slot 0.
      this.updateMenuItems(this.menuItems as MenuItem[]);
    }
  }

  /**
   * Set the SortOrder paramater based on what order the menu items are in. Eliminates any uneeded numbers between their sort orders.
   */
  private setSorting(): void {
    for (let i = 0; i < (this.menuItems as MenuItem[]).length; i++) {
      if ((this.menuItems as MenuItem[])[i].SortOrder >= 0) {
        (this.menuItems as MenuItem[])[i].SortOrder = i;
      }
    }
  }

  private sort(): void {
    (this.menuItems as MenuItem[]).sort((a, b) => (a.SortOrder < b.SortOrder ? -1 : 1));
  }

  /**
   * The grid only takes its own tiles. Pinning is a copy — the tile never left the grid — so a
   * nav-row item dragged back here has nothing to land on, and letting CDK move the element in
   * just flashes a nav-styled chip over the grid. Removing from the bar is the X button.
   */
  canDropInGrid = (drag: CdkDrag, drop: CdkDropList): boolean => drag.dropContainer === drop;

  /** Lets the nav row react (and explain itself) while a grid tile is in flight. */
  onGridDragStarted(item: MenuItem): void {
    this.mainMenuService.setDraggingGridApp(item);
  }

  onGridDragEnded(): void {
    this.mainMenuService.setDraggingGridApp(null);
  }

  drop = (event: CdkDragDrop<any>) => {
    if (event.previousContainer !== event.container) {
      return;
    }

    moveItemInArray(this.menuItems as MenuItem[], event.previousIndex, event.currentIndex);
    this.setSorting();
    this.updateMenuItems(this.menuItems as MenuItem[]);
  }

  // #endregion

  // #region Menu Actions

  unhideAll(): void {
    for (let item of this.menuItems as MenuItem[]) {
      if (item.SortOrder < 0) {
        item.SortOrder = 1000;
      }
    }
    this.setSorting();
    this.sort();
    this.updateMenuItems(this.menuItems as MenuItem[]);
  }

  unhide(menuItem: MenuItem): void {
    ((this.menuItems as MenuItem[]).find(a => a === menuItem) as MenuItem).SortOrder = 1000;
    this.setSorting();
    this.updateMenuItems(this.menuItems as MenuItem[]);
  }

  logout() {
    this.appService.clearAll();
    this.router.navigate(['logout']);
  }

  canImpersonate() {
    if (!this.userService.CurrentUserProfile || !this.oktaUserId)
      return false;

    if (this.userService.CurrentUserProfile.Roles && this.userService.CurrentUserProfile.Roles.some(x => x.RoleName === 'Impersonate' || x.RoleName === 'CanImpersonateStaff'))
      return true;

    if (this.oktaUserId !== this.userService.CurrentUserProfile.OktaUserId)
      return true;

    return false;
  }

  startMainMenuTour(): void {
    this.guidedTourService.startMainMenuTour();
  }

  startProfileTour(): void {
    this.guidedTourService.startProfileTour(this.userService.CurrentUserProfile?.UserType === 'Student');
  }

  startMessageTour(): void {
    this.guidedTourService.startMessageTour();
  }

  startPostTour(): void {
    this.postsTourService.startFeedTour();
  }

  startGuardianPostTour(): void {
    this.postsTourService.startGuardianFeedTour();
  }

  hideGuidedTour(): void {
    if (!this.userService.CurrentUserProfile) return;
    this.userService.CurrentUserProfile.ShowGuidedTour = false;
    this.userService.updateShowGuidedTourAsync(false).subscribe();
  }

  clearNotifications(): void {
    if (this.menuItems) {
      for (let item of this.menuItems) {
        item.Notifications = 0;
      }
    }
  }

  get hasStudents(): boolean {
    return (this.menuItems?.some(i => i.ItemType === MenuItemType.Student) ?? false);
  }

  // Visible school records per student (keyed by UserID), precomputed whenever the student
  // list changes so template bindings don't re-filter on every change-detection pass.
  private visibleRecordsByUser: Record<number, StudentInSchoolRecord[]> = {};

  // Active visible-record index per student (keyed by UserID) for the stacked school-record
  // carousel on the student cards. Not persisted — each visit starts every card back on its
  // first record.
  activeRecordIndex: Record<number, number> = {};

  private rebuildVisibleRecords(): void {
    this.visibleRecordsByUser = {};
    for (const student of this.parentPortalStudents) {
      this.visibleRecordsByUser[student.UserID] = this.getVisibleEnrollments(student.StudentInSchoolRecords);
    }
  }

  getVisibleRecords(student: StudentInfo): StudentInSchoolRecord[] {
    return this.visibleRecordsByUser[student.UserID] ?? [];
  }

  /**
   * Filters a student's school records down to the ones the card carousel should show,
   * mirroring the parent portal's getVisibleEnrollments rules (work item #92795), applied
   * in order:
   *  1. Secondary Enrollment (71) records are always removed — a dual-enrolled student's
   *     courses already surface under the primary school's card.
   *  2. If the student has any Enrolled (2) record, all ReEnrolled (9) records are dropped —
   *     next year's re-enrollment record is redundant while the current enrollment shows.
   *  3. Accepted (6) records are filtered by the parent's CRM seat decision: declined →
   *     hidden, confirmed → visible, undecided → only the first undecided record shows.
   *  4. Waitlist (7) records the parent declined are hidden, and only the first surviving
   *     waitlist record is kept (one waitlist card max).
   *  5. Everything else (Enrolled, Applied, Graduated, Withdrawn, NotApplicable) passes
   *     through.
   */
  private getVisibleEnrollments(records: StudentInSchoolRecord[] | undefined): StudentInSchoolRecord[] {
    if (!records?.length) return [];

    const hasEnrolled = records.some(r => r.MasterStatusID === EnrollmentMasterStatus.Enrolled);
    let acceptedShown = false;
    let waitlistShown = false;

    return records.filter(record => {
      switch (record.MasterStatusID) {
        case EnrollmentMasterStatus.SecondaryEnrollment:
          return false;
        case EnrollmentMasterStatus.ReEnrolled:
          return !hasEnrolled;
        case EnrollmentMasterStatus.Accepted: {
          const decision = record.CrmEnrollmentDecision;
          if (decision?.HasDeclinedSeat) return false;
          if (decision?.HasConfirmedSeat) return true;
          if (acceptedShown) return false;
          acceptedShown = true;
          return true;
        }
        case EnrollmentMasterStatus.Waitlist:
          if (record.CrmEnrollmentDecision?.WaitlistDecisionValue === 0) return false;
          if (waitlistShown) return false;
          waitlistShown = true;
          return true;
        default:
          return true;
      }
    });
  }

  getActiveRecordIndex(student: StudentInfo): number {
    const count = this.getVisibleRecords(student).length;
    const index = this.activeRecordIndex[student.UserID] ?? 0;
    // A refetch can shrink the visible list; fall back to the first record rather than none.
    return index < count ? index : 0;
  }

  getActiveRecord(student: StudentInfo): StudentInSchoolRecord | undefined {
    return this.getVisibleRecords(student)[this.getActiveRecordIndex(student)];
  }

  /**
   * The enrollment stage card for the student's active record, or null to show the regular
   * card rows. A stage card always replaces the regular card for its record (the portal's
   * student-card is a strict if/else); cycling the record pager flips between them.
   */
  getActiveStage(student: StudentInfo): EnrollmentCardStage | null {
    const record = this.getActiveRecord(student);
    if (!record) return null;
    const stage = determineCardStage(record, student.StudentInSchoolRecords ?? []);
    return stage === EnrollmentCardStage.None ? null : stage;
  }

  /**
   * Stage card click for every Accepted sub-state and Waitlist: the portal's
   * enrollment-verification page walks the parent through accepting/declining the
   * seat, checking the waitlist, and whatever comes next.
   */
  openEnrollmentVerification(): void {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    this.portalLink.open('enrollment-verification');
  }

  /** Registered stage card click: the portal's registration forms page for this record. */
  openRegistrationForms(record: StudentInSchoolRecord): void {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    this.portalLink.open(`student/${record.StudentID}/${record.StudentInSchoolID}/forms`);
  }

  /**
   * Registration-complete stage card click ("View Next Steps"): informational next-steps
   * dialog — nothing is saved, so it can be reopened any time.
   */
  openRegistrationCompleteDialog(student: StudentInfo, record: StudentInSchoolRecord): void {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    this.adaptiveDialog.open(RegistrationCompleteModalComponent, {
      width: '900px',
      data: { studentFirstName: student.FirstName, record }
    });
  }

  isAtLastRecord(student: StudentInfo): boolean {
    const count = this.getVisibleRecords(student).length;
    return count > 0 && this.getActiveRecordIndex(student) === count - 1;
  }

  // School 500 has no meal service, so its cards hide the meal-balance row.
  private static readonly NO_MEAL_SERVICE_SCHOOL_ID = 500;

  showMealBalance(student: StudentInfo, record: StudentInSchoolRecord): boolean {
    // Fallback students (portal gateway down) have no meal-balance data — hide the row
    // rather than showing a misleading $0.00.
    if (student.IsPortalDataUnavailable) return false;
    return record.SchoolID !== MainMenuComponent.NO_MEAL_SERVICE_SCHOOL_ID;
  }

  /** Opens the school's meal-payment site (link stamped per school by the server). */
  addToMealBalance(record: StudentInSchoolRecord): void {
    if (record.LunchMenuLink) {
      window.open(record.LunchMenuLink, '_blank');
    }
  }

  /**
   * Grade options for a school card's resources dialog: distinct grades among the guardian's
   * students at that school (empty for non-guardians — the dialog then shows the superset).
   */
  getGradeOptionsForSchool(school: School): GradeLevelOption[] {
    return gradeOptionsForSchool(this.parentPortalStudents, school.SchoolEID);
  }

  /** Navigates to the student's home page, same as clicking their Student menu item. */
  openStudentHome(student: StudentInfo): void {
    // UserID is 0 when the external student couldn't be matched to one of our users.
    if (!student.UserID) return;
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    this.router.navigate(['/apps/students/' + student.UserID]);
  }

  /** Steps a student's card to the previous/next visible school record, wrapping at the ends. */
  cycleRecord(student: StudentInfo, direction: 1 | -1): void {
    const count = this.getVisibleRecords(student).length;
    if (count < 2) return;
    this.activeRecordIndex[student.UserID] = (this.getActiveRecordIndex(student) + direction + count) % count;
  }

  // #endregion
  
}
